import { ChangeEvent, useEffect, useState } from 'react';
import { Table } from '@tanstack/react-table';
import { useDebouncedCallback } from 'use-debounce';

import { InputProps, SearchInput } from '@oaknorth/component-core-v2';
import { useTranslation } from '@oaknorth/util-intl';

const DEFAULT_DEBOUNCE = 200;

interface TableSearchControlProps<T extends object> {
  table: Table<T>;

  /**
   * defined delay to execute search functions (in milliseconds)
   */
  debounceDelay?: number;
  /**
   * define what to display as placeholder for the input
   */
  placeholder?: string;
  size?: InputProps['size'];
}

export const TableSearchControl = <T extends object = {}>({
  table,
  debounceDelay = DEFAULT_DEBOUNCE,
  placeholder,
  size,
}: TableSearchControlProps<T>) => {
  const { t } = useTranslation();
  const initialValue = table.getState().globalFilter;
  const [value, setValue] = useState(initialValue);

  // Sync the value with the table's filter if that changes externally
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const debouncedCallback = useDebouncedCallback((value: string) => {
    table.setGlobalFilter(value);
  }, debounceDelay);

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    setValue(event.target.value);
    debouncedCallback(event.target.value);
  }
  return (
    <SearchInput
      width="full"
      inputProps={{
        'data-testid': 'table-search',
        value: value || '',
        placeholder: placeholder || t`table.search.placeholder`,
        onChange: handleChange,
        size,
      }}
    />
  );
};
